import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  TrendingUp, 
  TrendingDown, 
  ArrowLeft,
  Plus,
  Minus,
  Building2,
  Globe,
  DollarSign,
  BarChart3,
  Newspaper,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import AppLayout from '../components/AppLayout';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const StockDetailPage = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  
  const [quote, setQuote] = useState(null);
  const [profile, setProfile] = useState(null);
  const [candles, setCandles] = useState([]);
  const [news, setNews] = useState([]);
  const [range, setRange] = useState('1M');
  const [loading, setLoading] = useState(true);
  const [position, setPosition] = useState(null);
  
  // Trade Dialog
  const [tradeDialog, setTradeDialog] = useState({ open: false, type: 'buy' });
  const [tradeAmount, setTradeAmount] = useState('');
  const [tradeShares, setTradeShares] = useState('');
  const [tradeMode, setTradeMode] = useState('amount'); // 'amount' or 'shares'
  const [executing, setExecuting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [quoteRes, profileRes, candlesRes, positionsRes, newsRes] = await Promise.all([
        api.get(`/stocks/${symbol}/quote`),
        api.get(`/stocks/${symbol}/profile`).catch(() => null),
        api.get(`/stocks/${symbol}/candles?time_range=${range}`),
        api.get('/portfolio/positions'),
        api.get(`/stocks/${symbol}/news?limit=5`).catch(() => ({ data: { news: [] } }))
      ]);
      
      setQuote(quoteRes.data);
      setProfile(profileRes?.data || null);
      setNews(newsRes.data?.news || []);
      
      // Transform candles for chart
      const chartData = candlesRes.data.candles.map(c => ({
        date: new Date(c.timestamp * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        price: c.close,
        high: c.high,
        low: c.low,
        volume: c.volume
      }));
      setCandles(chartData);
      
      // Check if user has position in this stock
      const userPosition = positionsRes.data.find(p => p.symbol === symbol.toUpperCase());
      setPosition(userPosition || null);
    } catch (error) {
      console.error('Failed to fetch stock data:', error);
      toast.error('Failed to load stock data');
    } finally {
      setLoading(false);
    }
  }, [api, symbol, range]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTrade = async () => {
    setExecuting(true);
    try {
      const payload = {
        symbol: symbol.toUpperCase(),
        trade_type: tradeDialog.type,
      };
      
      if (tradeMode === 'amount') {
        payload.amount = parseFloat(tradeAmount);
      } else {
        payload.shares = parseFloat(tradeShares);
      }
      
      const response = await api.post('/trades/execute', payload);
      toast.success(response.data.message);
      setTradeDialog({ open: false, type: 'buy' });
      setTradeAmount('');
      setTradeShares('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Trade failed');
    } finally {
      setExecuting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatPercent = (value) => {
    const prefix = value >= 0 ? '+' : '';
    return `${prefix}${value?.toFixed(2) || '0.00'}%`;
  };

  const formatVolume = (value) => {
    if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
    return value?.toString() || '0';
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="text-xs text-gray-500">{label}</p>
          <p className="text-sm font-semibold text-[#1A4D2E]">
            {formatCurrency(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  const ranges = ['1D', '5D', '1W', '1M', '3M', '6M', '1Y', '5Y'];

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-pulse-subtle text-primary font-serif text-xl">Loading stock data...</div>
        </div>
      </AppLayout>
    );
  }

  if (!quote) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <p className="text-gray-500 mb-4">Stock not found</p>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </AppLayout>
    );
  }

  const isPositive = quote.percent_change >= 0;

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="stock-detail-page">
        {/* Back Button & Header */}
        <div className="flex items-start justify-between">
          <div>
            <Button 
              variant="ghost" 
              onClick={() => navigate(-1)}
              className="mb-4 text-gray-500 hover:text-[#1A4D2E] -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-4">
              {profile?.logo && (
                <img 
                  src={profile.logo} 
                  alt={symbol} 
                  className="w-14 h-14 rounded-xl bg-white border border-stone-100 p-2"
                />
              )}
              <div>
                <h1 className="font-serif text-3xl font-semibold text-[#1F2937]" data-testid="stock-symbol">
                  {symbol.toUpperCase()}
                </h1>
                <p className="text-gray-500">{profile?.name || symbol}</p>
              </div>
            </div>
          </div>
          
          {/* Trade Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={() => setTradeDialog({ open: true, type: 'buy' })}
              className="bg-[#C1F23D] text-[#1A4D2E] hover:bg-[#b2e030] shadow-lg shadow-lime-400/20 font-bold"
              data-testid="buy-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Buy
            </Button>
            {position && (
              <Button
                onClick={() => setTradeDialog({ open: true, type: 'sell' })}
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50"
                data-testid="sell-btn"
              >
                <Minus className="w-4 h-4 mr-2" />
                Sell
              </Button>
            )}
          </div>
        </div>

        {/* Price Info */}
        <Card className="bg-white border-stone-100 shadow-card">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-end gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">Current Price</p>
                <p className="text-4xl font-bold text-[#1F2937] tabular-nums" data-testid="current-price">
                  {formatCurrency(quote.current_price)}
                </p>
              </div>
              <div className={`flex items-center gap-2 ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                {isPositive ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                <span className="text-xl font-semibold tabular-nums">
                  {formatCurrency(Math.abs(quote.change))} ({formatPercent(quote.percent_change)})
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-6 pt-6 border-t border-stone-100">
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Open</p>
                <p className="font-medium tabular-nums">{formatCurrency(quote.open)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Previous Close</p>
                <p className="font-medium tabular-nums">{formatCurrency(quote.previous_close)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Day High</p>
                <p className="font-medium tabular-nums">{formatCurrency(quote.high)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Day Low</p>
                <p className="font-medium tabular-nums">{formatCurrency(quote.low)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Chart */}
        <Card className="bg-white border-stone-100 shadow-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-serif text-xl text-[#1F2937]">Price Chart</CardTitle>
              <div className="flex gap-1 bg-stone-100 p-1 rounded-lg">
                {ranges.map((r) => (
                  <button
                    key={r}
                    onClick={() => setRange(r)}
                    className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                      range === r 
                        ? 'bg-white text-[#1A4D2E] shadow-sm' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid={`range-${r}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={candles}>
                  <defs>
                    <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1A4D2E" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#1A4D2E" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="date" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#6B7280' }}
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#6B7280' }}
                    tickFormatter={(value) => `$${value}`}
                    domain={['auto', 'auto']}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="price"
                    stroke="#1A4D2E"
                    strokeWidth={2}
                    fill="url(#priceGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Position & Company Info */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Your Position */}
          <Card className="bg-white border-stone-100 shadow-card">
            <CardHeader>
              <CardTitle className="font-serif text-xl text-[#1F2937]">Your Position</CardTitle>
            </CardHeader>
            <CardContent>
              {position ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Shares</p>
                      <p className="text-xl font-bold tabular-nums" data-testid="position-shares">{position.shares.toFixed(4)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Avg Cost</p>
                      <p className="text-xl font-bold tabular-nums">{formatCurrency(position.avg_cost)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Market Value</p>
                      <p className="text-xl font-bold tabular-nums">{formatCurrency(position.market_value)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Unrealized P&L</p>
                      <p className={`text-xl font-bold tabular-nums ${position.unrealized_pl >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {formatCurrency(position.unrealized_pl)} ({formatPercent(position.unrealized_pl_percent)})
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <BarChart3 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">You don't own any shares of {symbol}</p>
                  <Button 
                    onClick={() => setTradeDialog({ open: true, type: 'buy' })}
                    className="mt-4 bg-[#1A4D2E] hover:bg-[#143d24]"
                  >
                    Buy {symbol}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Company Info */}
          <Card className="bg-white border-stone-100 shadow-card">
            <CardHeader>
              <CardTitle className="font-serif text-xl text-[#1F2937]">Company Info</CardTitle>
            </CardHeader>
            <CardContent>
              {profile ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Industry</p>
                      <p className="font-medium">{profile.industry || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Exchange</p>
                      <p className="font-medium">{profile.exchange || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Market Cap</p>
                      <p className="font-medium">{profile.market_cap ? `$${formatVolume(profile.market_cap * 1e6)}` : 'N/A'}</p>
                    </div>
                  </div>
                  {profile.website && (
                    <a 
                      href={profile.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#1A4D2E] hover:underline text-sm"
                    >
                      <Globe className="w-4 h-4" />
                      Visit Website
                    </a>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">Company information not available</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* News Section */}
        {news.length > 0 && (
          <Card className="bg-white border-stone-100 shadow-card">
            <CardHeader>
              <CardTitle className="font-serif text-xl text-[#1F2937] flex items-center gap-2">
                <Newspaper className="w-5 h-5" />
                Latest News
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {news.map((article, index) => (
                  <a
                    key={article.id || index}
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 rounded-lg border border-stone-100 hover:border-[#1A4D2E]/20 hover:bg-green-50/30 transition-colors"
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <h4 className="font-medium text-[#1F2937] line-clamp-2 hover:text-[#1A4D2E]">
                          {article.headline}
                        </h4>
                        {article.summary && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{article.summary}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                          <span>{article.source}</span>
                          {article.datetime && (
                            <>
                              <span>•</span>
                              <span>{new Date(article.datetime).toLocaleDateString()}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </div>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trade Dialog */}
        <Dialog open={tradeDialog.open} onOpenChange={(open) => setTradeDialog({ ...tradeDialog, open })}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">
                {tradeDialog.type === 'buy' ? 'Buy' : 'Sell'} {symbol.toUpperCase()}
              </DialogTitle>
              <DialogDescription>
                Current price: {formatCurrency(quote.current_price)}
              </DialogDescription>
            </DialogHeader>
            
            <Tabs value={tradeMode} onValueChange={setTradeMode} className="mt-4">
              <TabsList className="w-full">
                <TabsTrigger value="amount" className="flex-1">By Amount ($)</TabsTrigger>
                <TabsTrigger value="shares" className="flex-1">By Shares</TabsTrigger>
              </TabsList>
              
              <TabsContent value="amount" className="mt-4">
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount (USD)</Label>
                  <Input
                    id="amount"
                    type="number"
                    placeholder="e.g., 1000"
                    value={tradeAmount}
                    onChange={(e) => setTradeAmount(e.target.value)}
                    className="text-lg"
                    data-testid="trade-amount-input"
                  />
                  {tradeAmount && (
                    <p className="text-sm text-gray-500">
                      ≈ {(parseFloat(tradeAmount) / quote.current_price).toFixed(4)} shares
                    </p>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="shares" className="mt-4">
                <div className="space-y-2">
                  <Label htmlFor="shares">Number of Shares</Label>
                  <Input
                    id="shares"
                    type="number"
                    placeholder="e.g., 10"
                    value={tradeShares}
                    onChange={(e) => setTradeShares(e.target.value)}
                    className="text-lg"
                    data-testid="trade-shares-input"
                  />
                  {tradeShares && (
                    <p className="text-sm text-gray-500">
                      ≈ {formatCurrency(parseFloat(tradeShares) * quote.current_price)}
                    </p>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            {tradeDialog.type === 'sell' && position && (
              <div className="flex items-center justify-between mt-2">
                <p className="text-sm text-gray-500">
                  Available to sell: {position.shares.toFixed(4)} shares
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-xs border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => {
                    setTradeShares(position.shares.toFixed(4));
                    setTradeAmount('');
                  }}
                  data-testid="sell-all-btn"
                >
                  Sell All
                </Button>
              </div>
            )}

            <DialogFooter className="mt-6">
              <Button
                variant="outline"
                onClick={() => setTradeDialog({ open: false, type: 'buy' })}
              >
                Cancel
              </Button>
              <Button
                onClick={handleTrade}
                disabled={executing || (!tradeAmount && !tradeShares)}
                className={tradeDialog.type === 'buy' 
                  ? 'bg-[#C1F23D] text-[#1A4D2E] hover:bg-[#b2e030]' 
                  : 'bg-red-500 hover:bg-red-600 text-white'
                }
                data-testid="confirm-trade-btn"
              >
                {executing ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin"></span>
                    Processing...
                  </span>
                ) : (
                  `Confirm ${tradeDialog.type === 'buy' ? 'Buy' : 'Sell'}`
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default StockDetailPage;
